import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import JibbleLinkPicker from "@/components/JibbleLinkPicker";
import StaffActionsMenu from "@/components/StaffActionsMenu";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, UserPlus, Clock, Edit3, Check, X } from "lucide-react";

const MANDATORY = ["Safeguarding", "Mental Capacity", "Moving and Handling", "Infection Control", "First Aid", "Fire Safety"];

export default function Staff() {
  const [items, setItems] = useState([]);
  const [recs, setRecs] = useState({});
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "care_worker" });
  const [tempPw, setTempPw] = useState("");

  const load = async () => {
    const { data } = await api.get("/staff");
    setItems(data.items || []);
    // gather recruitment for each
    const map = {};
    await Promise.all((data.items || []).map(async (u) => {
      try {
        const r = await api.get(`/staff/${u.id}`);
        map[u.id] = r.data.recruitment || {};
      } catch (_) {}
    }));
    setRecs(map);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/staff", form);
    setTempPw(data.temp_password || "");
    setForm({ email: "", full_name: "", role: "care_worker" });
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Staff &amp; recruitment</h1>
          <p className="text-sm text-slate-600">CQC Regulation 19 — fit and proper persons employed. DBS, references, training matrix.</p>
        </div>
        <button onClick={() => setCreating((v) => !v)} data-testid="new-staff-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
          <UserPlus className="h-4 w-4" /> Invite staff
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            {[
              "care_worker", "bank_worker", "team_leader", "administrator",
              "registered_manager", "deputy_manager", "compliance_manager", "nominated_individual",
            ].map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" data-testid="staff-submit-button" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Create staff account</button>
          </div>
        </form>
      )}

      {tempPw && <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm">Temporary password (share securely): <code className="font-mono">{tempPw}</code></div>}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><Clock className="h-4 w-4" /> Jibble Time &amp; Attendance linking</h3>
          <a href="/jibble" data-testid="jibble-dashboard-link" className="text-xs font-medium text-[#0D7377] hover:underline">Open attendance dashboard →</a>
        </div>
        <table className="w-full text-sm" data-testid="jibble-link-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 px-3">Staff</th>
              <th className="text-left py-2 px-3">Role</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Jibble link</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar userId={u.id} name={u.full_name} size={40} editable={true} bio={u.bio || ""} onSaved={load} />
                    <div>
                      <p className="font-medium text-slate-900">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                      {u.bio && <p className="text-[11px] text-slate-500 italic mt-0.5 max-w-[280px] truncate" title={u.bio}>{u.bio}</p>}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3 text-xs text-slate-600">
                  <RoleEditor
                    staff={u}
                    onSaved={(newRole) => setItems((cur) => cur.map((x) => (x.id === u.id ? { ...x, role: newRole } : x)))}
                  />
                </td>
                <td className="py-3 px-3">
                  <StatusBadge
                    status={u.status === "inactive" ? "overdue" : "active"}
                    label={u.status === "inactive" ? "Deactivated" : "Active"}
                  />
                </td>
                <td className="py-3 px-3">
                  <JibbleLinkPicker
                    staffId={u.id}
                    currentJibbleUserId={u.jibble_user_id}
                    onChanged={(jid) => {
                      setItems((cur) => cur.map((x) => (x.id === u.id ? { ...x, jibble_user_id: jid } : x)));
                    }}
                  />
                </td>
                <td className="py-3 px-3 text-right">
                  <StaffActionsMenu staff={u} onChanged={load} />
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">No staff records.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 overflow-auto">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Training matrix</h3>
        <table className="w-full text-sm" data-testid="training-matrix">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 px-3">Staff</th>
              <th className="text-left py-2 px-3">DBS</th>
              {MANDATORY.map((m) => <th key={m} className="text-left py-2 px-3">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const rec = recs[u.id] || {};
              const trainings = rec.training_records || [];
              const dbsDue = rec.dbs_renewal_due ? new Date(rec.dbs_renewal_due) : null;
              const dbsOk = dbsDue && dbsDue > new Date();
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-3">
                    <p className="font-medium text-slate-900">{u.full_name}</p>
                    <p className="text-xs text-slate-500 capitalize">{(u.role || "").replace(/_/g, " ")}</p>
                  </td>
                  <td className="py-3 px-3">
                    {rec.dbs_certificate_number ? (
                      <StatusBadge status={dbsOk ? "active" : "overdue"} label={dbsOk ? "OK" : "Renew"} />
                    ) : <StatusBadge status="pending" label="Missing" />}
                  </td>
                  {MANDATORY.map((m) => {
                    const t = trainings.find((tr) => (tr.course_name || "").toLowerCase().includes(m.toLowerCase()));
                    if (!t) return <td key={m} className="py-3 px-3"><StatusBadge status="overdue" label="Missing" /></td>;
                    const exp = t.expiry_date ? new Date(t.expiry_date) : null;
                    const within30 = exp && (exp - new Date()) < 30 * 86400000;
                    return <td key={m} className="py-3 px-3"><StatusBadge status={!exp ? "active" : exp < new Date() ? "overdue" : within30 ? "in_progress" : "active"} label={!exp ? "OK" : exp < new Date() ? "Expired" : within30 ? "Renew" : "OK"} /></td>;
                  })}
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={2 + MANDATORY.length} className="py-6 text-center text-slate-500">No staff records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ROLE_OPTIONS = [
  "care_worker", "bank_worker", "team_leader", "administrator",
  "compliance_manager", "deputy_manager", "registered_manager",
  "nominated_individual", "super_admin",
];

const ROLE_EDITORS = ["registered_manager", "deputy_manager", "nominated_individual", "super_admin"];

function RoleEditor({ staff, onSaved }) {
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(staff.role || "care_worker");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canEdit =
    ROLE_EDITORS.includes(currentUser?.role) &&
    currentUser?.id !== staff.id &&
    !(staff.role === "super_admin" && currentUser?.role !== "super_admin");

  const save = async () => {
    if (value === staff.role) { setEditing(false); return; }
    if (!window.confirm(`Change ${staff.full_name}'s role from "${(staff.role || "").replace(/_/g, " ")}" to "${value.replace(/_/g, " ")}"?`)) return;
    setBusy(true); setErr("");
    try {
      await api.put(`/staff/${staff.id}`, { role: value });
      onSaved(value);
      setEditing(false);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="capitalize">{(staff.role || "").replace(/_/g, " ")}</span>
        {canEdit && (
          <button
            onClick={() => setEditing(true)}
            data-testid={`edit-role-${staff.id}`}
            title="Change role"
            className="text-[#0D7377] hover:text-[#0A5C5F]"
          >
            <Edit3 className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`role-select-${staff.id}`}
        className="px-2 py-1 border border-slate-300 rounded text-xs"
        disabled={busy}
      >
        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
      </select>
      <button onClick={save} data-testid={`role-save-${staff.id}`} disabled={busy} className="text-emerald-700 hover:text-emerald-900 disabled:opacity-50">
        <Check className="h-4 w-4" />
      </button>
      <button onClick={() => { setEditing(false); setValue(staff.role); setErr(""); }} disabled={busy} className="text-slate-500 hover:text-slate-700">
        <X className="h-4 w-4" />
      </button>
      {err && <span className="text-xs text-red-600 ml-1">{err}</span>}
    </div>
  );
}
